/**
 * The fragment API.
 *
 * A fragment is a pure function of (context, state) that returns a detached
 * SVGElement. Fragments never attach listeners and never read app state: the
 * core owns pointer handling, hit-testing, transforms, and re-renders a single
 * fragment when its FragState changes.
 */
import type { CalEvent, ResolvedStyle, Source, StyleTokens, Tag } from '@circle/shared';
import type { Point } from '@circle/shared';
import { COLOUR_TOKENS } from './default/tokens';

export type FragmentKind =
  | 'ring'
  | 'band'
  | 'month'
  | 'day'
  | 'ringMark'
  | 'leader'
  | 'float'
  | 'cluster'
  | 'today';

/**
 * What every ring-borne fragment is told about the circle it is drawn on.
 *
 * A scene is a tree of rings now (docs/CIRCLES.md §2), each with its own centre
 * and radius. The centre is the ring group's `translate`, so a fragment never
 * sees it and keeps drawing around its own origin; the radius it does see,
 * because a ring at 0.4R has to draw itself at 0.4R rather than be squashed by
 * a transform. `ringId` is `'root'` or a tag id, and is what makes two
 * fragments of the same kind on different rings tell themselves apart.
 */
export interface OnRing {
  ringId: string;
  /** World radius of the ring this fragment is drawn on. */
  radius: number;
}

export interface FragState {
  hover: boolean;
  selected: boolean;
  dimmed: boolean;
  pinned: boolean;
}

export type Fragment<C> = (ctx: C, state: FragState) => SVGElement;

export const EMPTY_STATE: FragState = Object.freeze({
  hover: false,
  selected: false,
  dimmed: false,
  pinned: false,
});

// --- per-kind contexts ------------------------------------------------------

export interface RingCtx extends OnRing {
  year: number;
  /** The circle this ring stands for; absent on the root ("Everyone"). */
  tag?: Tag;
  style?: ResolvedStyle;
  /**
   * A parent whose children have split away: still drawn, faintly, holding
   * only the events tagged with it (docs/CIRCLES.md §2).
   */
  ghost?: boolean;
}

/**
 * One tree ring on a merged circle (docs/CIRCLES.md §2, D3-a): the radial slice
 * `ringBand` handed out, the tag it stands for, and the style that tag resolved
 * to. `bandCount === 1` is the degenerate case — the band is the whole disc and
 * the fragment draws nothing, so a single child looks exactly like no children.
 */
export interface BandCtx extends OnRing {
  bandIndex: number;
  bandCount: number;
  rInner: number;
  rOuter: number;
  tag?: Tag;
  style: ResolvedStyle;
}

export interface MonthCtx extends OnRing {
  /** 1..12 */
  index: number;
  name: string;
  startAngle: number;
  endAngle: number;
  year: number;
  /** A small ring: the wedge is still worth drawing, the label is not. */
  compact?: boolean;
  /**
   * The ring layer's current rotation, in radians. A month arc is drawn in
   * world coordinates but *read* on screen, so anything that has to stay
   * legible — a label along the arc — must decide from `angle + rotation`.
   * The scene refreshes it when the rotation settles, never per frame.
   */
  rotation?: number;
}

export interface DayCtx extends OnRing {
  year: number;
  month: number;
  day: number;
  angle: number;
  isToday: boolean;
  isWeekend: boolean;
}

export interface RingMarkCtx extends OnRing {
  event: CalEvent;
  angle: number;
  /**
   * The cascade collapsed for this event on this ring (docs/CIRCLES.md §5). Its
   * `prominence` sizes the mark; `colour` is what `ctx.colour` echoes.
   */
  style?: ResolvedStyle;
  /**
   * On a merged ring an event is drawn once, in its loudest tag's band; these
   * are the colours of the circles it is *also* in, drawn as membership dots
   * (docs/CIRCLES.md §2, D2-a). Empty for an event that is in one circle or none.
   */
  memberColours?: string[];
  /** Resolved colour: event style override, else the source colour. */
  colour?: string;
  source?: Source;
  /**
   * World units per screen pixel (1 / camera.scale). Marks ride the scaling
   * ring layer, so a theme that wants a legible mark at every zoom sizes it in
   * these. Refreshed with the layout pass, i.e. per ~5% of zoom.
   */
  screenScale: number;
}

export interface LeaderCtx {
  /** Anchor on the ring, world coords. */
  from: Point;
  /** Float attachment point (its near edge), world coords. */
  to: Point;
  /** Bend control point, world coords: on the anchor ray, just outside the ring. */
  bend: Point;
  /** The single event a float leader belongs to; absent for a cluster leader. */
  event?: CalEvent;
  colour?: string;
  /** The ring the anchor sits on. Float-layer coordinates are absolute, not ring-local. */
  ringId?: string;
}

/**
 * Floats are placed by the scene: it puts a slot group at the (rotated) world
 * position and scales it by 1 / camera.scale, so a float fragment draws around
 * its own origin **in screen pixels** and never applies its own translate.
 */
export interface FloatCtx {
  event: CalEvent;
  /** Ring anchor in world coords (where the leader starts), unrotated. */
  anchor: Point;
  /** Where the float sits, world coords, unrotated. */
  position: Point;
  /** Subtitle, e.g. the age turning in the view year. */
  ageText?: string;
  /** World units per screen pixel (1 / camera.scale) — floats are constant screen size. */
  screenScale: number;
  colour?: string;
  source?: Source;
  /** Upstream-deleted: drawn faded, still selectable. */
  orphaned?: boolean;
  /** The ring this float hangs off; absolute world coordinates either way. */
  ringId?: string;
  /** The cascade collapsed for this event on this ring; `prominence` sizes the card. */
  style?: ResolvedStyle;
}

/**
 * A run of floats that could not fit and collapsed into one card. Placed like a
 * float (own slot, screen pixels around the origin); `anchor` is the ring point
 * at the members' circular mean angle.
 */
export interface ClusterCtx {
  memberEvents: CalEvent[];
  /** `clusterLabel(titles)`, e.g. "Anna, Bo +3". */
  label: string;
  anchor: Point;
  position: Point;
  /** Members with no avatar of their own on the card — the "+N" pill. */
  hiddenCount: number;
  screenScale: number;
  /** Resolved colour per member, in `memberEvents` order. */
  colours?: string[];
  /** The ring this cluster hangs off. */
  ringId?: string;
}

export interface TodayCtx extends OnRing {
  angle: number;
}

// --- themes -----------------------------------------------------------------

export interface ThemeFragments {
  ring: Fragment<RingCtx>;
  band: Fragment<BandCtx>;
  month: Fragment<MonthCtx>;
  day: Fragment<DayCtx>;
  ringMark: Fragment<RingMarkCtx>;
  leader: Fragment<LeaderCtx>;
  float: Fragment<FloatCtx>;
  cluster: Fragment<ClusterCtx>;
  today: Fragment<TodayCtx>;
}

/**
 * Float box sizes a theme declares through its tokens. The scene turns these
 * screen pixels into world units for the layout algorithm; the fragments draw
 * to the same numbers. Missing or malformed tokens fall back to the defaults.
 */
export interface FloatSizing {
  /** Float card, screen px. */
  width: number;
  height: number;
  /** Cluster card, screen px. */
  clusterWidth: number;
  clusterHeight: number;
  /** Radial ring→float gap, world units. */
  gap: number;
  /** Floor on the gap measured in screen px (matters when zoomed out). */
  gapMinPx: number;
}

const SIZING_FALLBACK: FloatSizing = {
  width: 158,
  height: 46,
  clusterWidth: 150,
  clusterHeight: 44,
  gap: 96,
  gapMinPx: 28,
};

function token(theme: Theme, name: string, fallback: number): number {
  const raw = theme.tokens[name];
  const value = raw === undefined ? NaN : Number.parseFloat(raw);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

export function floatSizing(theme: Theme): FloatSizing {
  return {
    width: token(theme, '--float-w', SIZING_FALLBACK.width),
    height: token(theme, '--float-h', SIZING_FALLBACK.height),
    clusterWidth: token(theme, '--cluster-w', SIZING_FALLBACK.clusterWidth),
    clusterHeight: token(theme, '--cluster-h', SIZING_FALLBACK.clusterHeight),
    gap: token(theme, '--float-gap', SIZING_FALLBACK.gap),
    gapMinPx: token(theme, '--float-gap-min-px', SIZING_FALLBACK.gapMinPx),
  };
}

/**
 * A theme is a named set of fragments plus colour tokens. Tokens are written
 * onto the root SVG as CSS custom properties, so fragments only ever reference
 * `var(--token)` and never a literal colour.
 */
export interface Theme {
  name: string;
  tokens: Record<string, string>;
  fragments: ThemeFragments;
  /**
   * The theme's level of the style cascade (docs/CIRCLES.md §5) — second from
   * the top, above the calendar and below nothing but the app defaults. A theme
   * that only wants to swap fragments and CSS tokens leaves it out.
   */
  style?: StyleTokens;
  /** What the cascade starts from when nothing else has an opinion. */
  defaults?: ResolvedStyle;
  /** Alternates selectable per event via `style.ringMark` / `style.float`. */
  variants?: {
    ringMark?: Record<string, Fragment<RingMarkCtx>>;
    float?: Record<string, Fragment<FloatCtx>>;
  };
}

/**
 * The bottom of the style cascade: what a fragment gets when no theme,
 * calendar, circle, source or event has said anything (docs/CIRCLES.md §5).
 * `colour` is deliberately empty — the mark fragment already knows that a
 * colourless birthday is `var(--mark)` and a note is `var(--mark-note)`, and
 * spending the last level on one of them would silently recolour the other.
 */
export const STYLE_DEFAULTS: ResolvedStyle = Object.freeze({
  colour: '',
  ringMark: 'default',
  float: 'default',
  opacity: 1,
  labelFormat: 'default',
  avatarShape: 'circle',
  prominence: 1,
});

/** A theme's own starting point, or the app's. */
export function styleDefaults(theme: Theme): ResolvedStyle {
  return theme.defaults ?? STYLE_DEFAULTS;
}

/**
 * A CSS colour, and nothing else: hex, the rgb/hsl functions over numbers, or
 * a bare keyword. The value goes straight into `style.setProperty`, where a
 * `url()` or a `var()` would be a way to reach past the theme.
 */
const COLOUR_VALUE =
  /^(#[0-9a-f]{3,4}|#[0-9a-f]{6}|#[0-9a-f]{8}|(rgb|rgba|hsl|hsla)\(\s*[\d.]+%?(\s*[,\s]\s*[\d.]+%?){2}(\s*[,/]\s*[\d.]+%?)?\s*\)|[a-z]{3,24})$/i;

/**
 * The colour overrides a calendar carries on the wire (`Calendar.theme.tokens`),
 * kept to the names in {@link COLOUR_TOKENS} and to values that are colours.
 * Anything else is dropped rather than refused: a stale or foreign token
 * should leave the ring drawn in the theme's own colours, not undrawn.
 */
export function themeTokenOverrides(
  tokens: Record<string, string> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!tokens) return out;
  for (const [name, value] of Object.entries(tokens)) {
    if (!COLOUR_TOKENS.includes(name)) continue;
    if (typeof value !== 'string' || !COLOUR_VALUE.test(value.trim())) continue;
    out[name] = value.trim();
  }
  return out;
}

/** Name → theme. Themes register themselves when their module is imported. */
export const themes: Record<string, Theme> = {};

export function registerTheme(theme: Theme): Theme {
  themes[theme.name] = theme;
  return theme;
}

export const DEFAULT_THEME_NAME = 'default';

export function getTheme(name?: string): Theme {
  const theme = (name && themes[name]) || themes[DEFAULT_THEME_NAME];
  if (!theme) throw new Error('no themes registered — import fragments/default first');
  return theme;
}
